<script setup>
import { computed, onUnmounted, watch } from 'vue';
import { useStorage } from '@vueuse/core';
import { useMapGetter } from 'dashboard/composables/store';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

const accountId = useMapGetter('getCurrentAccountId');
const globalConfig = useMapGetter('globalConfig/get');
const currentUser = useMapGetter('getCurrentUser');
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);

const showSupport = computed(() => {
  return (
    isFeatureEnabledonAccount.value(
      accountId.value,
      FEATURE_FLAGS.CONTACT_CHATWOOT_SUPPORT_TEAM
    ) && globalConfig.value?.chatwootInboxToken
  );
});

const supportWidgetBaseUrl = computed(
  () => globalConfig.value?.supportWidgetBaseUrl
);

// Scope localStorage key to account to avoid cross-account leakage
const storageKey = computed(() => `cw_support_unread_${accountId.value || 0}`);
const unreadCount = useStorage(storageKey, 0);

const displayCount = computed(() => {
  if (!unreadCount.value) return '';
  return unreadCount.value < 100 ? `${unreadCount.value}` : '99+';
});

const isValidSupportUrl = url => {
  if (!url) return false;
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'https:' || parsed.protocol === 'http:';
  } catch {
    return false;
  }
};

const injectBubbleHideCSS = () => {
  if (document.getElementById('support-widget-bubble-hide')) return;
  const style = document.createElement('style');
  style.id = 'support-widget-bubble-hide';
  style.textContent = '#cw-bubble-holder { display: none !important; }';
  document.head.appendChild(style);
};

const identifyUser = () => {
  if (window.$chatwoot && currentUser.value) {
    const user = currentUser.value;
    window.$chatwoot.setUser(user.email || `user_${user.id}`, {
      name: user.name || user.available_name,
      email: user.email,
      avatar_url: user.avatar_url,
    });
  }
};

const loadSdkIfNeeded = () => {
  if (window.supportSdkLoaded || window.supportSdkLoading) return;

  const rawUrl = supportWidgetBaseUrl.value;
  if (!isValidSupportUrl(rawUrl)) return;

  window.supportSdkLoading = true;
  const baseUrl = rawUrl.replace(/\/+$/, '');

  window.chatwootSettings = {
    hideMessageBubble: true,
    position: 'left',
  };
  injectBubbleHideCSS();

  const script = document.createElement('script');
  script.src = `${baseUrl}/packs/js/sdk.js`;
  script.async = true;
  script.onload = () => {
    if (window.chatwootSDK) {
      window.chatwootSDK.run({
        websiteToken: globalConfig.value.chatwootInboxToken,
        baseUrl,
      });
      window.supportSdkLoaded = true;
      window.supportSdkLoading = false;

      const onReady = () => identifyUser();
      window.addEventListener('chatwoot:ready', onReady, { once: true });
      setTimeout(onReady, 3000);
    }
  };
  script.onerror = () => {
    window.supportSdkLoading = false;
  };
  document.head.appendChild(script);
};

// Only count messages from support agents, not the user's own messages.
// The SDK event.detail carries the full message data including sender_type.
// sender_type === 'User' means an agent sent the message (on the remote
// support installation, agents are "User" records).
const onNewMessage = event => {
  const data = event?.detail;
  if (!data) return;

  // Ignore messages sent by the contact (i.e. the current user themselves)
  // message_type 0 = incoming (from contact), 1 = outgoing (from agent)
  if (data.message_type === 0) return;

  if (!window.$chatwoot?.isOpen) {
    unreadCount.value += 1;
  }
};

// Reset unread count when widget is opened
const onWidgetOpen = () => {
  unreadCount.value = 0;
};

const handleClick = () => {
  if (window.$chatwoot) {
    window.$chatwoot.toggle('open');
    unreadCount.value = 0;
  }
};

// Only register listeners when support is actually enabled
watch(
  showSupport,
  enabled => {
    if (enabled) {
      loadSdkIfNeeded();
      window.addEventListener('chatwoot:on-message', onNewMessage);
      window.addEventListener('chatwoot:open', onWidgetOpen);
    } else {
      window.removeEventListener('chatwoot:on-message', onNewMessage);
      window.removeEventListener('chatwoot:open', onWidgetOpen);
    }
  },
  { immediate: true }
);

onUnmounted(() => {
  window.removeEventListener('chatwoot:on-message', onNewMessage);
  window.removeEventListener('chatwoot:open', onWidgetOpen);
});
</script>

<template>
  <button
    v-if="showSupport"
    v-tooltip.top="'Suporte'"
    class="size-8 rounded-lg hover:bg-n-alpha-1 flex-shrink-0 grid place-content-center relative"
    @click="handleClick"
  >
    <span class="i-lucide-life-buoy size-4" />
    <span
      v-if="displayCount"
      class="min-h-2 min-w-2 p-0.5 px-1 bg-n-ruby-9 rounded-lg absolute -top-1 -right-1.5 grid place-items-center text-[9px] leading-none text-n-ruby-3"
    >
      {{ displayCount }}
    </span>
  </button>
</template>
